import { SyntheticEvent, useCallback, useEffect, useState } from "react";

// Interfaces
import { ISideTree, ISideTreeChild } from "./interfaces";

// Helpers
import { getTreeItem, getItemId, getTreeIdentifiers, isLeafInPath, getSelectedNodes, getParentPathNodes, findRootTree } from "./helpers";

// MUI
import { Divider } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view";

// Components
import SideTreeLoader from "./SideTreeLoader";
import SideTreeChild from "./SideTreeChild";
import SideTreeLabelHeader from "./SideTreeLabelHeader";

const RenderInnerTree = (props: Readonly<ISideTreeChild>) => {
	const {
		parent = undefined,
		parentId = "",
		tree = [],
		treeIdentifiers = null,
		selectedNodes = [],
		level = 0,
		selectBy = "_id",
		showBy = "title",
		disabled = false,
		additionalSelected = [],
		defaultSelected = "",
	} = props;

	if (parent) {
		const isDisabled = disabled || parent?.disabled;
		const hasSelectedLeaf = isLeafInPath(treeIdentifiers, parentId, selectedNodes);

		return (
			<SideTreeChild
				parent={parent}
				parentId={parentId}
				level={level}
				showBy={showBy}
				selected={false}
				treeIdentifiers={treeIdentifiers}
				hasSelectedLeaf={hasSelectedLeaf}
				disabled={isDisabled}
			>
				<RenderInnerTree
					parentId={parentId}
					level={level + 1.5}
					tree={tree}
					treeIdentifiers={treeIdentifiers}
					selectedNodes={selectedNodes}
					additionalSelected={additionalSelected}
					defaultSelected={defaultSelected}
					selectBy={selectBy}
					showBy={showBy}
					disabled={disabled}
				/>
			</SideTreeChild>
		);
	}

	return tree.map((item: ISideTreeChild, key) => {
		const isLeaf = !item?.list?.length;
		const itemKey = getItemId(parentId, item?.[selectBy as keyof object], key);
		const isItemDisabled = disabled || item?.disabled;
		if (isLeaf) {
			const isSelected = selectedNodes?.includes(itemKey);
			const isAdditionalSelected = additionalSelected?.some(
				(additionalSelected2: string) => isSelected && additionalSelected2 === itemKey
			);
			return (
				<SideTreeChild
					key={itemKey}
					parentId={itemKey}
					parent={item}
					level={level}
					showBy={showBy}
					selected={isSelected}
					isAdditionalSelected={isAdditionalSelected}
					treeIdentifiers={treeIdentifiers}
					disabled={isItemDisabled}
					menu={item?.menu}
					defaultSelected={defaultSelected}
				/>
			);
		}

		return (
			<RenderInnerTree
				key={itemKey}
				parentId={itemKey}
				level={level + 1.5}
				parent={item}
				selectedNodes={selectedNodes}
				tree={item?.list || []}
				treeIdentifiers={treeIdentifiers}
				selectBy={selectBy}
				showBy={showBy}
				disabled={isItemDisabled}
				additionalSelected={additionalSelected}
				defaultSelected={defaultSelected}
			/>
		);
	});
};

const isMultipleSelection = (selectedItems: string[] | undefined, selected?: string) => Boolean(selectedItems && !selected);

const getAdditionalSelected = (selectedItems: string[] | undefined, selected?: string) => {
	if (!selectedItems?.length || !selected) {
		return [];
	}

	const additionalSelection = selectedItems.filter((item) => !item.includes(selected));
	return additionalSelection || [];
};

export default function SideTreeView(props: Readonly<ISideTree>) {
	const {
		label = undefined,
		tree = [],
		expanded = [],
		selectedItems = undefined,
		selected = "",
		selectBy = "_id",
		showBy = "title",
		height = "100%",
		loadingRows = 6,
		disabled = false,
		divider = false,
		loading = false,
		defaultSelected = "",
		onControlledClickAction = () => {},
		onSelectAction = () => {},
		onExpandedAction = () => {},
	} = props;

	const [nodes, setNodes] = useState<string[]>([]);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [expandedTree, setExpandedTree] = useState<string[]>([]);

	// Internal Tree Identifiers references
	const multipleSelection = isMultipleSelection(selectedItems, selected);
	const treeIdentifiers = getTreeIdentifiers({ tree, selectBy });
	const selectedNodes = getSelectedNodes({ tree: treeIdentifiers, isMultiple: multipleSelection, selectedIds, nodes });
	const additionalSelectedIds = getAdditionalSelected(selectedNodes, defaultSelected);

	const handleSelection = (_event: SyntheticEvent, itemId: string) => {
		const isSelectable = !multipleSelection && !selectedIds?.includes(itemId) && !selectedNodes?.includes(itemId);

		if (isSelectable) {
			const item = getTreeItem(tree ?? [], selectBy, itemId);

			if (item?.["controlled" as keyof object]) {
				onControlledClickAction?.(item);
				return;
			}

			if (item) {
				setSelectedIds([itemId]);
				onSelectAction?.(item, item?.[selectBy as keyof object]);
			}
		}
	};

	const handleMultipleSelection = (_event: SyntheticEvent, itemIds: string | string[] | null) => {
		const isSelectable = multipleSelection && Array.isArray(itemIds);
		if (isSelectable) {
			const items = itemIds
				.map((itemId) => {
					const item = getTreeItem(tree ?? [], selectBy, itemId);
					if (!item) {
						return null;
					}
					return {
						item,
						treeItemId: itemId,
					};
				})
				.filter(Boolean);

			if (!items?.length) {
				return;
			}

			const treeItemIds = items.map((element) => element?.treeItemId ?? "").filter(Boolean);
			const previousIds = [...new Set([...selectedIds, ...selectedNodes])];

			const isSimilar = previousIds?.length && previousIds.some((id) => treeItemIds.includes(id));

			//When is Multiple Selection and the previous selection is similar to the current selection
			if (isSimilar && previousIds.length >= 2) {
				const itemsSelected = items.map((element) => element?.item).filter((element) => element) as object[];
				setSelectedIds(previousIds);
				onSelectAction?.(itemsSelected, previousIds);
			}

			if (!isSimilar) {
				const itemsSelected = items.map((element) => element?.item).filter((element) => element) as object[];
				const itemsSelectedIds = itemsSelected.map((item: object) => item[selectBy as keyof object]);
				setSelectedIds(treeItemIds);
				onSelectAction?.(itemsSelected, itemsSelectedIds);
			}
		}
	};

	const handleExpanded = (_event: SyntheticEvent, expandedItems: string[]) => {
		setExpandedTree(expandedItems);

		const items = expandedItems
			.map((itemId) => {
				const item: object | null = findRootTree(treeIdentifiers, itemId);
				return item?.["referenceId" as keyof object] ?? "";
			})
			.filter(Boolean);

		onExpandedAction?.(items);
	};

	// Default expanded callback handler
	const handleExpandedDefault = useCallback((treeIdent: object | null, nodesExp: string[]) => {
		if (!nodesExp?.length) {
			return;
		}
		const nodesExpanded = getParentPathNodes(treeIdent, nodesExp);
		if (nodesExpanded?.length) {
			setExpandedTree(nodesExpanded);
		}
	}, []);

	useEffect(() => {
		// Default expanded
		handleExpandedDefault(treeIdentifiers, expanded);
	}, [expanded, treeIdentifiers, handleExpandedDefault]);

	useEffect(() => {
		// Default selection
		const isMultiple = isMultipleSelection(selectedItems, selected);
		const initialNodes = isMultiple ? [...new Set(selectedItems)] : [selected];
		setNodes(initialNodes);

		return () => {
			setNodes([]);
			setSelectedIds([]);
		};
	}, [selectedItems, selected]);

	if (loading) {
		return <SideTreeLoader length={loadingRows} divider={divider} hasLabel={Boolean(label)} />;
	}

	return (
		<SimpleTreeView
			className={"!-ml-[23px] !w-[105%]"}
			itemChildrenIndentation={0}
			multiSelect={multipleSelection}
			selectedItems={selectedNodes}
			expandedItems={expandedTree}
			disabledItemsFocusable
			disableSelection={disabled}
			sx={{
				height,
				maxHeight: "560px",
				maxWidth: 360,
				overflowY: "auto",
			}}
			onSelectedItemsChange={handleMultipleSelection}
			onItemSelectionToggle={handleSelection}
			onExpandedItemsChange={handleExpanded}
		>
			<SideTreeLabelHeader {...label} treeProps={props} />
			<RenderInnerTree
				tree={tree}
				level={0}
				treeIdentifiers={treeIdentifiers}
				selectedNodes={selectedNodes}
				additionalSelected={additionalSelectedIds}
				expanded={expandedTree}
				selectBy={selectBy}
				showBy={showBy}
				disabled={disabled}
			/>
			{divider && (
				<Divider
					className="!border-disabled"
					variant="middle"
					sx={{ marginLeft: "34px", marginTop: "15px", marginBottom: "15px", marginRight: "auto" }}
				/>
			)}
		</SimpleTreeView>
	);
}
